import { useCallback, useEffect, useReducer, useRef } from 'react';

import client from '../../shared/api/client';
import { toastError } from '../../shared/toastHelper';
import { ALL_COLUMNS, DEFAULT_HISTORY, DEFAULT_LAST_SHOW, DEFAULT_STATUS, DEFAULT_VISIBLE, LAST_SHOW_OPTIONS, SLIDERS } from './constants';

const defaultFilters = () => ({
  q: '',
  genre: [],
  agency: [],
  tag: [],
  history: [...DEFAULT_HISTORY],
  status: [...DEFAULT_STATUS],
  lastShow: DEFAULT_LAST_SHOW,
  avgCap: null,
  facebook: null,
  spotify: null,
  hasMetrics: false,
  sort: 'spotify',
  dir: 'desc',
});

const defaultColumns = () =>
  [...DEFAULT_VISIBLE, ...ALL_COLUMNS.map((c) => c.key).filter((k) => !DEFAULT_VISIBLE.includes(k))].map((key) => ({
    key,
    visible: DEFAULT_VISIBLE.includes(key),
  }));

// --- URL <-> state ---------------------------------------------------------

const parseRange = (raw) => {
  if (!raw) return null;
  const parts = raw.split('-').map((n) => parseInt(n, 10));
  if (parts.length !== 2 || parts.some(Number.isNaN)) return null;
  return parts;
};

const initFromUrl = () => {
  const filters = defaultFilters();
  const params = new URLSearchParams(window.location.search);

  if (params.get('q')) filters.q = params.get('q');
  if (params.get('genre')) filters.genre = params.get('genre').split(',').filter(Boolean);
  if (params.get('agency')) filters.agency = params.get('agency').split(',').filter(Boolean);
  if (params.get('tag')) filters.tag = params.get('tag').split(',').filter(Boolean);
  if (params.get('history')) filters.history = params.get('history').split(',').filter(Boolean);
  if (params.get('status')) filters.status = params.get('status').split(',').filter(Boolean);
  if (LAST_SHOW_OPTIONS.some((o) => o.key === params.get('last_show'))) filters.lastShow = params.get('last_show');
  SLIDERS.forEach((s) => { filters[s.key] = parseRange(params.get(s.param)); });
  if (params.get('metrics') === '1') filters.hasMetrics = true;
  if (params.get('sort')) filters.sort = params.get('sort');
  if (params.get('dir')) filters.dir = params.get('dir') === 'asc' ? 'asc' : 'desc';

  let columns = defaultColumns();
  const cols = params.get('cols');
  if (cols) {
    const visibleOrder = cols.split(',').filter((k) => ALL_COLUMNS.some((c) => c.key === k));
    const rest = ALL_COLUMNS.map((c) => c.key).filter((k) => !visibleOrder.includes(k));
    columns = [
      ...visibleOrder.map((key) => ({ key, visible: true })),
      ...rest.map((key) => ({ key, visible: false })),
    ];
  }

  const view = params.get('view') === 'suggestions' ? 'suggestions' : 'all';

  return { filters, columns, view, rows: [], total: 0, facets: {}, loading: true, error: false, nonce: 0 };
};

const serializeUrl = (filters, columns, view) => {
  const params = new URLSearchParams();
  if (view === 'suggestions') params.set('view', 'suggestions');
  if (filters.q) params.set('q', filters.q);
  if (filters.genre.length) params.set('genre', filters.genre.join(','));
  if (filters.agency.length) params.set('agency', filters.agency.join(','));
  if (filters.tag.length) params.set('tag', filters.tag.join(','));
  if (filters.history.length !== DEFAULT_HISTORY.length) params.set('history', filters.history.join(','));
  if (filters.status.length !== DEFAULT_STATUS.length) params.set('status', filters.status.join(','));
  if (filters.lastShow !== DEFAULT_LAST_SHOW) params.set('last_show', filters.lastShow);
  SLIDERS.forEach((s) => { if (filters[s.key]) params.set(s.param, filters[s.key].join('-')); });
  if (filters.hasMetrics) params.set('metrics', '1');
  if (filters.sort !== 'spotify' || filters.dir !== 'desc') {
    params.set('sort', filters.sort);
    params.set('dir', filters.dir);
  }
  const visible = columns.filter((c) => c.visible).map((c) => c.key);
  if (visible.join(',') !== DEFAULT_VISIBLE.join(',')) params.set('cols', visible.join(','));
  return params.toString();
};

const buildRequestParams = (filters, offset) => {
  const params = { offset, sort: filters.sort, dir: filters.dir };
  if (filters.q) params.q = filters.q;
  if (filters.genre.length) params.genre = filters.genre;
  if (filters.agency.length) params.agency = filters.agency;
  if (filters.tag.length) params.tag = filters.tag;
  if (filters.history.length) params.history = filters.history;
  if (filters.status.length) params.status = filters.status;
  if (filters.lastShow !== DEFAULT_LAST_SHOW) params.last_show_within = filters.lastShow;
  SLIDERS.forEach((s) => {
    if (filters[s.key]) {
      [params[`${s.param}_min`], params[`${s.param}_max`]] = filters[s.key];
    }
  });
  if (filters.hasMetrics) params.has_metrics = '1';
  return params;
};

// --- reducer ---------------------------------------------------------------

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_FILTER':
      return { ...state, filters: { ...state.filters, ...action.patch } };
    case 'RESET_FILTERS':
      return { ...state, filters: defaultFilters() };
    case 'SET_COLUMNS':
      return { ...state, columns: action.columns };
    case 'SET_VIEW':
      return { ...state, view: action.view };
    case 'LOAD_START':
      return { ...state, loading: true, error: false };
    case 'LOAD_SUCCESS':
      return {
        ...state,
        loading: false,
        error: false,
        // scroll ("load more") fetches omit total/facets — keep the values from
        // the first page rather than clobbering them with undefined.
        total: action.payload.total ?? state.total,
        facets: action.payload.facets ?? state.facets,
        rows: action.append ? [...state.rows, ...action.payload.rows] : action.payload.rows,
      };
    case 'LOAD_ERROR':
      return { ...state, loading: false, error: true };
    case 'RELOAD':
      return { ...state, nonce: state.nonce + 1 };
    default:
      return state;
  }
};

export default function useArtistBrowse(dataUrl) {
  const [state, dispatch] = useReducer(reducer, undefined, initFromUrl);
  const { filters, columns } = state;
  const fetchKey = JSON.stringify(filters);
  // Monotonic request generation: any fetch resolving after the filters changed
  // (its gen no longer current) is dropped, so stale rows can't land.
  const genRef = useRef(0);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    // Suggestions mode renders its own data (useSmartSuggestions); the browse grid
    // isn't shown there, so don't refetch /artists/data on every filter change.
    // Still load once when facets are empty so a direct ?view=suggestions entry
    // has the rail's options populated.
    const facetsLoaded = Object.keys(state.facets).length > 0;
    if (state.view === 'suggestions' && facetsLoaded) return;

    genRef.current += 1;
    const gen = genRef.current;
    loadingMoreRef.current = false;
    dispatch({ type: 'LOAD_START' });
    client
      .get(dataUrl, { params: buildRequestParams(filters, 0) })
      .then((res) => { if (gen === genRef.current) dispatch({ type: 'LOAD_SUCCESS', payload: res.data, append: false }); })
      .catch(() => {
        if (gen !== genRef.current) return;
        dispatch({ type: 'LOAD_ERROR' });
        toastError('Could not load artists. Please try again.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey, state.nonce, state.view]);

  useEffect(() => {
    const qs = serializeUrl(filters, columns, state.view);
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey, columns, state.view]);

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    const gen = genRef.current;
    const offset = state.rows.length;
    dispatch({ type: 'LOAD_START' });
    client
      .get(dataUrl, { params: buildRequestParams(state.filters, offset) })
      .then((res) => { if (gen === genRef.current) dispatch({ type: 'LOAD_SUCCESS', payload: res.data, append: true }); })
      .catch(() => {
        if (gen !== genRef.current) return;
        dispatch({ type: 'LOAD_ERROR' });
        toastError('Could not load more artists.');
      })
      .finally(() => { loadingMoreRef.current = false; });
  }, [dataUrl, state.rows.length, state.filters]);

  const setFilter = useCallback((patch) => dispatch({ type: 'SET_FILTER', patch }), []);
  const setColumns = useCallback((cols) => dispatch({ type: 'SET_COLUMNS', columns: cols }), []);
  const resetFilters = useCallback(() => dispatch({ type: 'RESET_FILTERS' }), []);
  const reload = useCallback(() => dispatch({ type: 'RELOAD' }), []);
  const setView = useCallback((view) => dispatch({ type: 'SET_VIEW', view }), []);

  return {
    ...state,
    hasMore: state.rows.length < state.total,
    setFilter,
    setColumns,
    resetFilters,
    loadMore,
    reload,
    setView,
  };
}
